import { Content, type ContentType } from '@ilteducation/content-registry-types';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useBooks } from '@ilteducation/books';
import { SearchContext, SearchQuery } from './types';
import { searchContent } from './api';
import { getQueryObject, getQueryString } from './query-formatters';

type SearchContextState<C extends Content> = {
  contentType: ContentType;
  searchQuery: SearchQuery;
  pageTotal: number;
  isLoading: boolean;
  contents: C[];
};

type State<C extends Content> = {
  [contextId: string]: SearchContextState<C> | undefined;
};

export const createSearchId = (contentType: ContentType, searchQuery: SearchQuery) =>
  `${contentType}:${getQueryString(searchQuery).slice(1)}`;

export const parseSearchId = (contextId: string): SearchContext => {
  const parts = contextId.split(/:(.*)/s);
  return { contentType: parts[0] as ContentType, searchQuery: getQueryObject(parts[1]) };
};

const useSearchContexts = <C extends Content>(contentType: ContentType) =>
  defineStore(`content-search-contexts-${contentType}`, () => {
    const contextState = ref<State<C>>({});

    const context = computed(() => (contextId: string) => contextState.value[contextId]);
    const result = computed(() => (contextId: string) => contextState.value[contextId]?.contents ?? []);
    const isLoading = computed(() => (contextId: string) => contextState.value[contextId]?.isLoading ?? false);
    const hasMore = computed(() => (contextId: string) => {
      return contextState.value[contextId]?.searchQuery?.pagination?.cursor !== null;
    });
    const pageTotal = computed(() => (contextId: string) => contextState.value[contextId]?.pageTotal ?? 0);

    const search = (searchQuery: SearchQuery, limit: number) => {
      const contextId = createSearchId(contentType, searchQuery);

      if (isLoading.value(contextId)) {
        return;
      }
      const contextValue = context.value(contextId);
      if (
        contextValue !== undefined &&
        (contextValue.contents.length >= limit || contextValue.contents.length === contextValue.pageTotal)
      ) {
        return;
      }

      const query: SearchQuery = {
        ...searchQuery,
      };
      if (limit) {
        query.pagination = {
          ...searchQuery?.pagination,
          size: limit,
        };
      }

      contextState.value = {
        ...contextState.value,
        ...{
          [contextId]: {
            contentType,
            searchQuery: query,
            pageTotal: 0,
            isLoading: true,
            contents: [],
          },
        },
      };

      searchContent<C>(contentType, query)
        .then(({ data, cursor, total }) => {
          contextState.value = {
            ...contextState.value,
            [contextId]: {
              contentType,
              searchQuery: {
                ...contextState.value[contextId]?.searchQuery,
                ...{ pagination: { cursor, size: limit } },
              },
              pageTotal: total,
              isLoading: false,
              contents: data,
            },
          };

          const { addBooks } = useBooks();
          addBooks(data);
        })
        .catch(() => {
          contextState.value = {
            ...contextState.value,
            [contextId]: {
              ...contextState.value[contextId]!,
              isLoading: false,
            },
          };
        });
    };

    const next = (contextId: string) => {
      if (
        !contextState.value ||
        !contextState.value[contextId] ||
        isLoading.value(contextId) ||
        !hasMore.value(contextId)
      ) {
        return;
      }

      contextState.value = {
        ...contextState.value,
        [contextId]: {
          ...contextState.value[contextId]!,
          isLoading: true,
        },
      };

      searchContent<C>(contextState.value[contextId]!.contentType, contextState.value[contextId]!.searchQuery)
        .then(({ data, cursor }) => {
          contextState.value = {
            ...contextState.value,
            [contextId]: {
              ...contextState.value[contextId]!,
              searchQuery: {
                ...contextState.value[contextId]!.searchQuery,
                ...{ pagination: { cursor, size: contextState.value[contextId]!.searchQuery.pagination?.size || 100 } },
              },
              isLoading: false,
              contents: [...contextState.value[contextId]!.contents, ...data],
            },
          };
        })
        .catch(() => {
          contextState.value = {
            ...contextState.value,
            [contextId]: {
              ...contextState.value[contextId]!,
              isLoading: false,
            },
          };
        });
    };

    const clearSearch = () => {
      contextState.value = {};
    };

    return {
      search,
      next,
      clearSearch,
      context,
      result,
      isLoading,
      hasMore,
      total: pageTotal,
    };
  })();

export default useSearchContexts;
